import './table-tree-arrow.css';

import { ActionButton, ActionsGroup, Table } from '@elotech/components';
import React, {
  Children,
  ComponentProps,
  ReactElement,
  ReactNode,
  useMemo
} from 'react';

import { useExpandedIds } from '../../../hooks';

type ColumnProps<T> = Omit<ComponentProps<typeof Table.Column>, 'value'> & {
  value(item: T, siblings: T[], parent?: T): ReactNode;
};

type MultilevelColumnType<T> = ReactElement<ColumnProps<T>>;

export function MultilevelColumn<T>(
  props: ColumnProps<T>
): MultilevelColumnType<T> {
  return <Table.Column<T> {...props} value={props.value as any} />;
}

type Props<T> = {
  values: T[];
  childrenExtractor(item: T): T[];
  keyExtractor(item: T): string;
  children: (MultilevelColumnType<T> | undefined)[] | MultilevelColumnType<T>;
  isRowLoading?(item: T): boolean;
  allowExpand?(item: T): boolean;
  highlightError?(item: T, siblings: T[]): boolean;
  footer?: ReactNode;
  loading?: boolean;
  cellClassName?(item: T): string;
};

export function TableMultilevel<T>({
  values,
  childrenExtractor,
  keyExtractor,
  children,
  allowExpand = () => true,
  loading,
  isRowLoading,
  highlightError,
  footer,
  cellClassName
}: Props<T>) {
  const { expandedIds: collapsedIds, handleExpandedIds: handleCollapsedIds } =
    useExpandedIds();

  const columns = useMemo(
    () =>
      Children.toArray(children)
        .filter(Boolean)
        .map(x => (x as MultilevelColumnType<T>).props),
    [children]
  );

  const collapse = (item: T) => {
    const itemKey = keyExtractor(item);
    const isCollapsed = collapsedIds.includes(itemKey);
    handleCollapsedIds(itemKey);
    if (isCollapsed) {
      return;
    }

    childrenExtractor(item)?.forEach(collapse);
  };

  const renderItems = (items: T[], parent?: T, level = 0) => {
    return (
      <>
        {items.map((item, index) => {
          const itemKey = keyExtractor(item);
          const itemChildren = childrenExtractor(item);
          const canExpand = itemChildren.length > 0 && allowExpand(item);
          const isExpanded = !collapsedIds.includes(itemKey) && canExpand;
          const isLoading = loading || isRowLoading?.(item);

          const classNames = Object.entries({
            reduced: true,
            'placeholder-loader': isLoading,
            open: isExpanded,
            father: canExpand,
            error: highlightError?.(item, items)
          })
            .filter(([_, value]) => value)
            .map(([key]) => key)
            .join(' ');

          return (
            <React.Fragment key={`${itemKey}-${index}`}>
              <tr id={itemKey} className={classNames}>
                {columns.map((column, columnIndex) => (
                  <td
                    key={`${itemKey}-${columnIndex}-${column.name}`}
                    className={[
                      column.align || '',
                      column.className || '',
                      cellClassName?.(item) || ''
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    colSpan={columnIndex === columns.length - 1 ? 2 : 1}
                    title={`${column.header}`}
                    onClick={() =>
                      columnIndex === 0 && canExpand && collapse(item)
                    }
                  >
                    <b className="td-label">{column.header}</b>
                    <span className="td-content">
                      {column.value(item, items, parent)}
                    </span>
                  </td>
                ))}
              </tr>
              {isExpanded && renderItems(itemChildren, item, level + 1)}
            </React.Fragment>
          );
        })}
      </>
    );
  };

  const expandCollapseAll = () => {
    if (collapsedIds.length > 0) {
      collapsedIds.forEach(id => handleCollapsedIds(id));
    } else {
      handleCollapsedIds(values.map(keyExtractor));
    }
  };

  return (
    <div className="panel-table-outer">
      <table className="panel-table tree tree-arrow compact-table">
        <thead>
          <tr className={loading ? 'placeholder-loading' : ''}>
            {columns.map((column, index) => (
              <th key={`${index}-${column.name}`} className={column.className}>
                <span className="th-content">{column.header}</span>
              </th>
            ))}
            <th className="header-button " data-position-scroll="">
              <ActionsGroup>
                <ActionButton
                  icon={`chevron-${collapsedIds.length ? 'down' : 'up'}`}
                  onClick={expandCollapseAll}
                  label={`${
                    collapsedIds.length ? 'Expandir' : 'Colapsar'
                  } todos`}
                />
              </ActionsGroup>
            </th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr className="placeholder-loading">
              {columns.map((column, index) => (
                <td key={`${index}-${column.name}`}>&nbsp;</td>
              ))}
              <td />
            </tr>
          ) : (
            renderItems(values)
          )}
        </tbody>
        {footer && (
          <tfoot>
            <tr>
              <td colSpan={columns.length + 1}>{footer}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
